// Shared scroll/animation bus — the single scroll listener for the page.
//
// Design (this is the fix for the prototype's frozen-mid-state cinematic):
//   • ONE passive scroll listener + ONE passive resize listener.
//   • All work happens inside ONE requestAnimationFrame tick, so multiple
//     consumers never each schedule their own competing rAF/scroll handlers.
//   • Subscribers receive (scrollY, dtSeconds) and may return `true` to
//     request another frame. That keeps the loop alive AFTER scroll events
//     stop, letting eased values converge to their target instead of parking
//     mid-animation (the prototype eased per scroll-EVENT, so any flick or
//     abrupt stop froze the hero half-morphed).
//   • When nobody wants another frame the loop stops — zero idle work.
//
// Discipline for subscribers: do DOM reads first, writes last, and write only
// transform/opacity-driving CSS custom properties (compositor-friendly).

const scrollSubs = new Set();
const resizeSubs = new Set();

let rafId = 0;
let lastT = 0;
let listening = false;

function frame(t) {
  rafId = 0;
  // Clamp dt: background tabs / long pauses would otherwise teleport easings.
  const dt = lastT ? Math.min(0.05, (t - lastT) / 1000) : 1 / 60;
  lastT = t;
  const y = window.scrollY;
  let again = false;
  for (const fn of scrollSubs) {
    try {
      if (fn(y, dt) === true) again = true;
    } catch {
      /* one broken subscriber must not kill the page loop */
    }
  }
  if (again) schedule();
  else lastT = 0;
}

function schedule() {
  if (!rafId) rafId = requestAnimationFrame(frame);
}

function onResize() {
  for (const fn of resizeSubs) {
    try {
      fn();
    } catch {
      /* keep loop alive */
    }
  }
  schedule();
}

function ensureListening() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
}

/**
 * Subscribe to the shared scroll frame. `fn(scrollY, dt)` runs at most once
 * per animation frame; return `true` from it to request a follow-up frame
 * (e.g. while an eased value is still converging). Returns unsubscribe.
 */
export function subscribeScroll(fn) {
  ensureListening();
  scrollSubs.add(fn);
  schedule();
  return () => scrollSubs.delete(fn);
}

/** Subscribe to window resize (measurement invalidation). Returns unsubscribe. */
export function subscribeResize(fn) {
  ensureListening();
  resizeSubs.add(fn);
  return () => resizeSubs.delete(fn);
}

/** Force a frame (call after state changes that move layout, e.g. modal close). */
export function kickScroll() {
  ensureListening();
  schedule();
}
